import {
  Component,
  Input,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  OnInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogService, NbDialogRef } from '@nebular/theme';
import { LocalDataSource } from 'ng2-smart-table';
import { isObservable } from 'rxjs';
import { Subject } from 'rxjs/Subject';
import * as moment from 'moment';

import { FoodData } from '../../../../@core/interfaces/common/CalorieFriend/food';
import { Meal, MealItem } from '../../../../@core/interfaces/common/CalorieFriend/meal';
import { MealPlanDay } from '../../../../@core/interfaces/common/CalorieFriend/plan-day';
import { NativeAppService } from 'app/@core/backend/common/services/native-app.service';
import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';

import { CalculateMetrics, CopyMealItem } from 'app/BuildOps';
import { AdjustMetricsGraphComponent } from 'app/admin/public-foods/meal-metrics-graph/meal-metrics-graph.component';

import { MealComponent } from '../PlanMeals/Meal/meal.component';
import { MealPresentationComponent } from '../PlanMeals/Meal/MealLayout/MealPresentation/meal-presentation.component';
import { format } from '../../../../../support';
import { FormConstants } from '../../CustomUIOps';
import { Patient } from 'app/@core/interfaces/common/CalorieFriend/patients';

/**
 * Base class for Meal Plan Day components
 */
export class MealPlanDayComponentBase
  extends BaseComponentCanDeactivate
  implements OnInit, AfterViewInit {
    @Input() Patient: Patient
  DayLabel = '';
  NewDay = $localize`:@@NewDay:New Day`;
  EditDay = $localize`:@@EditDay:Edit Day`;
  NameLabel = $localize`:@@Meal:Meal`;
  TimeLabel = $localize`:@@Time:Time`;
  MealLabel = 'Meal Name';

  PlanID = null;
  ShowSubmit = false;

  ParentPlanID: number = null;

  @Input('Day') Day: MealPlanDay;
  @Input('PlanDays') PlanDays: Array<MealPlanDay> = null;
  @Input('PlanView') PlanView: boolean = false;
  @Input('HidePlanName') HidePlanName: boolean = false;

  DayForm: FormGroup;
  MealSource: LocalDataSource;

  MealCals = 0;
  MealProtein = 0;
  MealCarbs = 0;
  MealNetCarbs = 0;
  MealFats = 0;
  NewDayView = false;
  isDirty = false;
  isNativeApp = false;

  @ViewChildren('MealPresentation')
  private MealPresentationViews: QueryList<MealPresentationComponent>;
  @ViewChild('DayMetricsGraphView')
  DayMetricsGraphView: AdjustMetricsGraphComponent = null;

  DayNotesWidth = '80%';
  DayNotesHeight = '200px';

  FormConstants = FormConstants;

  /**
   * Settings of the ng2 smart table of Meals
   */
  MealTableSettings = {
    mode: 'external',
    actions: {
      position: 'right',
      add: false,
    },
    edit: {
      editButtonContent:
        '<img src="assets/icons/added_oultine_modify_pencil.svg" class="img-archive" width="24" height="24">',
    },
    delete: {
      deleteButtonContent:
        '<img src="assets/icons/trash-2-outline.svg" class="img-archive" width="24" height="24">',
    },
    columns: {
      name: { title: this.NameLabel, type: 'string', filter: false },
      time: { title: this.TimeLabel, type: 'string', filter: false },
    },
  };

  protected readonly unsubscribe$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private dialogService: NbDialogService,
    private chdetRef: ChangeDetectorRef,
    private FoodService: FoodData,
    private nativeApp: NativeAppService
  ) {
    super(dialogService);
  }

  get DayName() {
    return this.DayForm.get('DayName');
  }
  get DayNotes() {
    return this.DayForm.get('DayNotes');
  }
  get assignWeekDays() {
    return this.DayForm.get('assignWeekDays');
  }

  get IsDirty() {
    let childrenDirty = false;
    this.MealPresentationViews.forEach(
      (c) => (childrenDirty = childrenDirty || c.IsDirty)
    );
    return (
      this.isDirty ||
      childrenDirty ||
      (this.DayMetricsGraphView != null && this.DayMetricsGraphView.IsDirty)
    );
  }

  resetDirty() {
    super.resetDirty();
    this.DayMetricsGraphView.resetDirty();
    this.MealPresentationViews.forEach((c) => c.resetDirty());
  }

  /**
   * Initializes the component.
   * - Initializes the Day form.
   * - Gets the Plan ID from the URL
   * - Initalizes the Meals data source
   */
  ngOnInit(): void {
    // this.MealsOrdered();
    this.InitDayForm();

    this.isNativeApp = this.nativeApp.isFromApp();

    if (!this.Day) {
      this.Day = {
        id: null,
        meals: new Array<Meal>(),
        name: '',
        planID: this.ParentPlanID,
        notes: '',
        dayOfWeek: [],
        createMeal: false,
        order: 1,
      };

      this.ShowSubmit = true;
    } else {
      this.DayForm.patchValue({
        DayName: this.Day.name,
        DayNotes: this.Day.notes,
        assignWeekDays: this.Day.dayOfWeek,
      });

      this.ShowSubmit = false;
    }

    this.MealSource = new LocalDataSource(this.Day.meals); // create the source

    this.PlanID = this.route.snapshot.paramMap.get('PlanId');

    if (this.Day.id !== null && this.Day.id > 0) {
      this.DayLabel = this.EditDay;
    } else {
      this.DayLabel = this.NewDay;
      this.NewDayView = true;
    }

    if (!this.Day.meals) {
      this.Day.meals = new Array<Meal>();
    }

    if (
      this.Day != null &&
      this.Day !== undefined &&
      this.Day.createMeal &&
      this.Day.meals.length === 0
    ) {
      this.NewMeal();
    }

    this.PrepareDayObservables();
    this.PrepareMetricsTotalDayMetrics();
    super.resetDirty();
  }

  /**
   * AfterViewInit implementation.
   * Updates the Metrics Graph for the day whenever view updates.
   */
  ngAfterViewInit() {
    this.updateMealGraphUpdateBinding();
  }

  updateMealGraphUpdateBinding() {
    if (this.MealPresentationViews) {
      this.MealPresentationViews.forEach((view) => {
        view.ItemUpdateNotification.subscribe((data) => {
          this.PrepareDayObservables();
          this.DayMetricsGraphView.PrepareMealItems();
          this.PrepareMetricsTotalDayMetrics();

          this.chdetRef.detectChanges();
        });
      });
    }
  }

  /**
   * Method to initialize the Day form.
   */
  InitDayForm() {
    this.DayForm = this.fb.group({
      DayName: this.fb.control('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(50),
      ]),
      DayNotes: this.fb.control('', [Validators.maxLength(500)]),
      assignWeekDays: this.fb.control([]),
    });
    // this.initForm(this.DayForm);
  }

  /**
   * Method to open a New Meal component as a pop up window to add a new meal
   */
  NewMeal() {
    let Time = '6:00 AM';

    if (this.Day.meals.length > 0) {
      Time = this.Day.meals[this.Day.meals.length - 1].time;
    }

    const NewTime = moment(Time, 'hh:mm a').toDate();
    NewTime.setHours(NewTime.getHours() + 2);
    NewTime.setMinutes(NewTime.getMinutes());
    const TimeString = format(
      '{0}:{1} {2}',
      NewTime.getHours() > 12 ? NewTime.getHours() - 12 : NewTime.getHours(),
      NewTime.getMinutes() < 10
        ? '0' + NewTime.getMinutes()
        : NewTime.getMinutes(),
      NewTime.getHours() >= 12 ? 'PM' : 'AM'
    );

    const NewMeal: Meal = {
      id: -1,
      dayID: this.Day.id,
      name: format('{0}', this.MealLabel),
      time: TimeString,
      items: null,
    };

    this.UpdateMeal(NewMeal);

    this.chdetRef.detectChanges();

    this.updateMealGraphUpdateBinding();
  }

  /**
   * Function to update the Day's meal list with the {meal} data.
   * @usageNotes
   * It is called after adding a new meal or editing an existing one to update the meals list.
   * @param meal Meal object
   */
  UpdateMeal(meal: Meal) {
    if (meal !== null && meal !== undefined) {
      // check if meal is in list --
      const found = this.Day.meals.find((element) => element === meal);
      if (!found) {
        this.Day.meals.push(meal);
      }

      if (this.DayMetricsGraphView) this.DayMetricsGraphView.PrepareMealItems();
      this.PrepareMealItemsObservables(meal);
      this.PrepareMetricsTotalDayMetrics();

      this.MealSource.refresh();

      this.isDirty = true;
    }
  }

  /**
   * Event handler of 'Edit' action.
   * Opens the Meal component to edit an existing meal and updates the meals list.
   * @param $event Event object
   */
  onEdit($event) {
    this.dialogService
      .open(MealComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          TheMeal: $event.data,
          ExistingMeals: this.Day.meals,
          DayID: this.Day.id,
        },
      })
      .onClose.subscribe((meal) => this.UpdateMeal(meal));
  }


  Copy(block: Meal) {
    MealPresentationComponent.clipboard = block;
  }


  Paste(block: Meal, index:Number) {
    const list: MealItem[] = [];

    if (MealPresentationComponent.clipboard != null) {
      MealPresentationComponent.clipboard.items.forEach((item) => {
        const newItem = CopyMealItem(item);
        newItem.mealID = block.id;
        list.push(newItem);
      });
    }


    this.MealPresentationViews.find(
      (y) => y.Index === index
    ).MealItemsView.UpdateSelectedItem(list);

    MealPresentationComponent.clipboard = null;
  }

  HasClipboard(): boolean {
    return MealPresentationComponent.clipboard != null;
  }


  /**
   * Removes the selected meal
   */
  RemoveMeal(meal) {
    if (meal.items.length === 0) {
      this.removeMealCore(meal);
      return;
    }

    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this meal?' },
      })
      .onClose.subscribe((result) => {
        if (result) {
          this.removeMealCore(meal);
        }
      });
  }

  removeMealCore(meal) {
    this.Day.meals = this.Day.meals.filter((m) => m !== meal);
    this.MealSource = new LocalDataSource(this.Day.meals); // create the source

    this.PrepareMetricsTotalDayMetrics();

    this.isDirty = true;
  }

  /**
   * Checks if Meal can be removed from the Day
   */
  get CanRemoveMeal() {
    if (this.Day.meals && this.Day.meals && this.Day.meals.length > 1)
      return true;
    return false;
  }

  Accept() { }

  Back() {
    // this.ref.close();
  }

  /**
   * Method to sort the meals list by its time
   */
  MealsOrdered() {
    if (
      this.Day !== null &&
      this.Day !== undefined &&
      this.Day.meals !== null &&
      this.Day.meals !== undefined &&
      this.Day.meals.length > 0
    ) {
      this.Day.meals.sort((a: Meal, b: Meal) => (a.time > b.time ? 1 : -1));
    }
  }

  /**
   * Method to prepare observables of the meals list by preparing observables for individual meal items
   */
  PrepareDayObservables() {
    // for the meal items  -- configure an observable
    // place an observable on the object

    if (this.Day !== null && this.Day !== undefined) {
      if (
        this.Day.meals !== null &&
        this.Day.meals !== undefined &&
        this.Day.meals.length > 0
      ) {
        for (const meal of this.Day.meals) {
          this.PrepareMealItemsObservables(meal);
        }
      }
    }
  }

  /**
   * Method to prepare an observable of the {meal} object.
   * Creates a 'Update' sujbect to subscribe with the callback which update the Metrics Graph.
   * @param meal Meal object
   */
  private PrepareMealItemsObservables(meal) {
    if (meal && meal.items) {
      for (const item of meal.items) {
        if (!isObservable(item.Update)) item.Update = new Subject();

        item.Update.subscribe((data) => {
          this.PrepareMetricsTotalDayMetrics();
        });
      }
    }
  }

  /**
   * Method to update Metrics graph data.
   */
  PrepareMetricsTotalDayMetrics() {
    this.MealCals = 0;
    this.MealProtein = 0;
    this.MealCarbs = 0;
    this.MealNetCarbs = 0;
    this.MealFats = 0;

    if (this.Day !== null && this.Day !== undefined) {
      if (
        this.Day.meals !== null &&
        this.Day.meals !== undefined &&
        this.Day.meals.length > 0
      ) {
        for (const meal of this.Day.meals) {
          if (meal.items) {
            for (const item of meal.items) {
              const itemMetrics = CalculateMetrics(item);
              this.MealCals += itemMetrics.MealCals;
              this.MealProtein += itemMetrics.MealProtein;
              this.MealCarbs += itemMetrics.MealCarbs;
              this.MealNetCarbs += itemMetrics.MealNetCarbs;
              this.MealFats += itemMetrics.MealFats;
            }
          }
        }
      }
    }
  }

  /**
   * Event handler for Name field
   * @param $event Event object
   */
  NameChange($event) {
    this.Day.name = this.DayName.value;
  }

  /**
   * Event handler for Notes field
   * @param $event Event object
   */
  NotesChange($event) {
    this.Day.notes = $event.target.value;
  }

  onAssignWeekDaysChagne($event): void {
    if ($event.target) return;
    this.Day.dayOfWeek = $event;
  }

  ViewPlan() { }

  private MonitorMealChanges() {
    if (
      this.Day != null &&
      this.Day !== undefined &&
      this.Day.meals != null &&
      this.Day.meals !== undefined
    ) {
    }
  }
}

/**
 * Component for Plan Day View.
 * Inherits from the PlanDayComponentBase component.
 */
@Component({
  selector: 'app-plan-day-view',
  templateUrl: './plan-day.component.html',
  styleUrls: ['./plan-day.component.scss', '../../global.component.scss'],
})
export class MealPlanDayComponent extends MealPlanDayComponentBase {
  constructor(
    protected PlanDayRef: NbDialogService,
    PlanDayRoute: ActivatedRoute,
    PlanDayFB: FormBuilder,
    PlanDayDialogService: NbDialogService,
    cdRef: ChangeDetectorRef,
    PlanFoodService: FoodData,
    nativeApp: NativeAppService
  ) {
    super(
      PlanDayRoute,
      PlanDayFB,
      PlanDayDialogService,
      cdRef,
      PlanFoodService,
      nativeApp
    );
  }
}

@Component({
  selector: 'app-plan-mobile-day',
  templateUrl: './plan-day-mobile.component.html',
  styleUrls: ['./plan-day.component.scss', '../../global.component.scss'],
})
export class MealPlanDayMobileLayoutComponent extends MealPlanDayComponentBase { }

/**
 * Component for showing Plan Day in a dialog.
 * Inherits from the PlanDayComponentBase component.
 */
@Component({
  selector: 'app-plan-day-dialog',
  templateUrl: './plan-day.component.html',
  styleUrls: ['./plan-day.component.scss', '../../global.component.scss'],
})
export class MealPlanDayDialogComponent extends MealPlanDayComponentBase {
  constructor(
    protected PlanDayService: NbDialogService,
    protected PlanDayRef: NbDialogRef<MealPlanDayDialogComponent>,
    PlanDayRoute: ActivatedRoute,
    PlanDayFB: FormBuilder,
    cdRef: ChangeDetectorRef,
    PlanFoodService: FoodData,
    nativeApp: NativeAppService
  ) {
    super(
      PlanDayRoute,
      PlanDayFB,
      PlanDayService,
      cdRef,
      PlanFoodService,
      nativeApp
    );
  }

  Accept() {
    if (this.Day.meals !== null && this.Day.meals.length > 0) {
      this.Day.name = this.DayName.value;
      this.Day.notes = this.DayNotes.value;
      this.Day.dayOfWeek = this.assignWeekDays.value;
      this.PlanDayRef.close(this.Day);
    }
  }
}

/**
 * Component for static Plan Day view.
 * Inherits from the PlanDayComponentBase component.
 */
@Component({
  selector: 'app-plan-static-day',
  templateUrl: './plan-day.component.html',
  styleUrls: ['./plan-day.component.scss', '../../global.component.scss'],
})
export class MealPlanDayStaticComponent extends MealPlanDayComponentBase {
  constructor(
    PlanDayRoute: ActivatedRoute,
    PlanDayFB: FormBuilder,
    cdRef: ChangeDetectorRef,
    PlanDayDialogService: NbDialogService,
    PlanFoodService: FoodData,
    nativeApp: NativeAppService
  ) {
    super(
      PlanDayRoute,
      PlanDayFB,
      PlanDayDialogService,
      cdRef,
      PlanFoodService,
      nativeApp
    );
    this.PlanView = true;
  }
}

/**
 * Component for Plan Day Report.
 * Inherits from the PlanDayComponentBase component.
 */
@Component({
  selector: 'app-plan-report-day',
  templateUrl: './plan-day.report-component.html',
  styleUrls: ['./plan-day.component.scss'],
})
/** plan-day component*/
export class MealPlanDayReportComponent extends MealPlanDayComponentBase {
  constructor(
    PlanDayRoute: ActivatedRoute,
    PlanDayFB: FormBuilder,
    cdRef: ChangeDetectorRef,
    PlanDayDialogService: NbDialogService,
    PlanFoodService: FoodData,
    nativeApp: NativeAppService
  ) {
    super(
      PlanDayRoute,
      PlanDayFB,
      PlanDayDialogService,
      cdRef,
      PlanFoodService,
      nativeApp
    );
  }
}
